import { createRoute, useNavigate } from "@tanstack/react-router";
import { Store, Trash2 } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopCart } from "./desktop/cart.tsx";
import { readPurchaseAs, readViewAs, writeIdentity } from "../lib/identity.ts";
import { Card } from "@/components/ui/card";
import { TabPage } from "../components/layout.tsx";
import { Button, Empty, ErrorBlock, Img, Price, QtyStepper, Spinner, fenInt } from "../components/ui.tsx";
import {
  useCart,
  useMe,
  useRemoveFromCart,
  useSetCartQuantity,
} from "../api/queries.ts";

function Cart() {
  const navigate = useNavigate();
  const { data: me, isPending: mePending } = useMe();
  const { data, isPending, error, refetch } = useCart(Boolean(me));
  const setQty = useSetCartQuantity();
  const remove = useRemoveFromCart();

  if (mePending) return <TabPage><Spinner /></TabPage>;

  if (!me) {
    return (
      <TabPage>
        <Empty title="登录后查看购物车" hint="登录后才能保存你选的货" action={{ label: "去登录", to: "/login" }} />
      </TabPage>
    );
  }

  if (isPending) return <TabPage><Spinner /></TabPage>;
  if (error) return <TabPage><ErrorBlock error={error} onRetry={() => refetch()} /></TabPage>;

  const empty = data.groups.length === 0;

  /**
 * 🔴 **预览「我卖的价」时不能下单。**
 *    那个价是客户看到的，按它下单会生成一张"自己卖给自己"的单，
 *    而且跟服务端按真实身份算出的价对不上，会被 `PRICE_CHANGED` 拦下。
 *    与其让他点了报错，不如在按钮上就说清楚。
 */
const previewing = readPurchaseAs() !== null && readViewAs() === "shop";

  return (
    <TabPage>
      <header
        className="border-b border-neutral-200 bg-white px-4 py-3"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      >
        <h1 className="text-[17px] font-medium">购物车</h1>
      </header>
      {/**
       * ⚠️ 车里有下架或缺货的商品时不整页报错，只把有问题的那几行标出来 ——
       *    一个商品下架不该让整个购物车打不开。
       */}
      {data.issues.length > 0 && (
        <div className="mx-4 mt-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {data.issues.map((i) => (
            <p key={i.skuId}>
              {i.title ? `「${i.title}」` : "有商品"}
              {i.reason}
            </p>
          ))}
        </div>
      )}

      {empty ? (
        <Empty title="购物车是空的" hint="去挑点建材吧" action={{ label: "去逛逛", to: "/products" }} />
      ) : (
        <>
          {/* ⚠️ 按供应商分组展示 —— 下单时就是按这个拆单的，先让用户看明白 */}
          {data.groups.map((g) => (
            <Card key={g.supplierOrgId} className="mx-3 mt-3 overflow-hidden">
              <h2 className="flex items-center gap-1.5 border-b border-border px-4 py-2.5 text-[13px] text-muted-foreground">
                <Store className="size-3.5" />
                {g.supplierName}
              </h2>

              {g.lines.map((line) => (
                <div key={line.skuId} className="flex gap-3 border-b border-border px-3 py-3">
                  <Img
                    src={line.imageUrl}
                    alt={line.title}
                    className="size-20 shrink-0 rounded-lg object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-[14px] leading-snug">{line.title}</p>
                    <p className="mt-1 text-brand-600">
                      <Price cents={line.unitPrice} className="text-[16px]" />
                    </p>
                    <div className="mt-2 flex items-center justify-between">
                      <QtyStepper
                        value={line.quantity}
                        max={line.stock}
                        onChange={(q) => setQty.mutate({ skuId: line.skuId, quantity: q })}
                      />
                      <button
                        className="flex size-10 items-center justify-center text-muted-foreground active:opacity-60"
                        onClick={() => remove.mutate(line.skuId)}
                        aria-label="删除"
                      >
                        <Trash2 className="size-[18px]" />
                      </button>
                    </div>
                    {!line.available && (
                      <p className="mt-1 text-[12px] text-destructive">库存仅剩 {line.stock}</p>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex justify-between px-4 py-2.5 text-[13px]">
                <span className="text-muted-foreground">
                  小计（含运费 <Price cents={g.freightAmount} />）
                </span>
                <Price cents={g.payableAmount} className="text-brand-600" />
              </div>
            </Card>
          ))}

          <div
            className="fixed inset-x-0 bottom-14 z-20 mx-auto flex w-full max-w-[480px] items-center gap-3 border-t border-neutral-200 bg-white px-4 py-2"
            style={{ marginBottom: "env(safe-area-inset-bottom)" }}
          >
            <div className="flex-1">
              <p className="text-sm text-ink-500">
                合计 <Price cents={data.totalPayable} className="text-lg text-brand-600" />
              </p>
              {/*
                🔴 有店铺优惠时把它顶到最显眼的位置 ——
                   用户在购物车里最关心的就是"我省了多少"。
                ⚠️ 没优惠时（游客、或商家没让利）退回原来的"货款+运费"，
                   显示一个 −¥0 只会让人困惑。
              */}
              {data.totalShopDiscount > 0 ? (
                <p className="text-xs text-brand-600">
                  已为你省 <Price cents={data.totalShopDiscount} />
                  <span className="ml-1.5 text-muted-foreground line-through">
                    {fenInt(data.totalOriginal)}
                  </span>
                </p>
              ) : (
                <p className="text-xs text-ink-500">
                  货款 <Price cents={data.totalGoods} /> + 运费{" "}
                  <Price cents={data.totalFreight} />
                </p>
              )}
            </div>
            {/* ⚠️ 同商品详情：预览态给一条出路，不给死按钮。 */}
            <Button
              size="lg"
              onClick={
                previewing ? () => writeIdentity("") : () => navigate({ to: "/checkout" })
              }
            >
              {previewing ? "按我的进货价结算" : "去结算"}
            </Button>
          </div>
        </>
      )}
    </TabPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function CartRoute() {
  return useIsDesktop() ? <DesktopCart /> : <Cart />;
}

export const cartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cart",
  component: CartRoute,
});
